import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {when} from 'lit/directives/when.js';
import {conditional} from '../../../util/conditional';
import store from '../../../store';
import {classes} from '../../../../../common/tpl/directives';
import {filterAndJoin} from '../../../util/filterAndJoin';

import {hasFeatureValue, pimText, pimTextDebug} from '../../../util/values';
import {
    selectCRI, selectDiameter, selectIsOnesided,
    selectIsSingle,
    selectSystemLumen,
    selectSystemLumenPerPower,
    selectSystemLumenString,
    selectSystemPower,
    selectSystemPowerString,
	selectWeight,
	selectWindsurfacearea,
	selectVariantCodeTypes
} from '../../../selectors';

@customElement('page-summary')
export class PageSummary extends connect(store)(LitElement) {

	static styles = css`
	.summary {
		display: flex;
		margin: var(--specsheet-medium-gap) var(--specsheet-large-gap);
	}

	.summaryContainer {
		width: 49%;
	}

	.description {
		width: 48%;
	}

	.imageContainer {
		text-align: center;
		width: 50%;
		height: 95%;
		max-height: 870px;
		margin-left: auto;
		margin-right: auto;
	}

	@media (min-width: 768px) {
		.summaryContainer {
			width: 50%;
		}
	}

	@media (max-width: 768px) {
		.summaryContainer {
			width: 44%;
		}
	}

	@media (print) {

		.summaryContainer {
			width: 45%;
		}

		.summary {
			display: flex;
			padding-top: 5vw;
			width: 100vw;
		}

		.imageContainer {
			text-align: center;
			width: 50vw;
			max-height: 770px;
			height: 90%;
		}
	}

	@-moz-document url-prefix(){
		.imageContainer {
			text-align: center;
			width: 20vw;
			max-height: 770px;
			height: 85%;
			margin-bottom: 10px;
		}
	}
	`;

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.featuremodel = state.featureModel;
		this.productData = state.productData;
		this.status = state.status;
		this.lang = state.status.lang;
		this.decisions = state.decisions.data;
		this.conditionalVariantValues = state.productData.conditionalVariantValues;
	}

	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.state.decisions.data, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.state.decisions.data, this.state.productData.translations, value, prefix, suffix);
	}
	c(id) {
		return conditional(this.decisions, this.conditionalVariantValues, id);
	}


	render() {
		const s = this.state;
		const shouldDisplayFeature = (value) => value !== undefined && value !== '';
		const descAtt = this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV');
		const isDescAttPresent = descAtt !== undefined;

		const applicableStandards = [
			this.t('LU_IP.IP66', 'DV.S'),
			this.t('LU_IK.10', 'DV.S'),
			this.t('EB_ElectricalProtectionClass_1', 'FV.S'),
			this.t('LU_ENEC_CertificateDescription.ENEC', 'DV.S'),
			this.t('LU_CE.CE', 'DV.S'),
		  ];
		  const filteredStandards = applicableStandards.filter(value => value !== undefined && value !== '');
		  const isStandardsPresent = filteredStandards.length > 0;


		return html`
            	<div class="summary">
					<div class="summaryContainer">
						<div class="imageContainer">
                        	${when(selectIsSingle(s),
                                () => html`
                                    <svg-optics-specsheet></svg-optics-specsheet>`,
                                () => html`
                                    <svg-productimage></svg-productimage>
                                `)}
                    	</div>
                	</div>

                	<div class="description">

                    <specsheet-featuregroup>
					${shouldDisplayFeature(this.t('HS_HousingType', 'FV.S')) ?
					html`
                        <specsheet-feature class="specsheet">" >
                            <!-- Modell -->
                            <trans-label slot="label" key="HS_HousingType" context="F"></trans-label>
                            <trans-label slot="value" key="HS_HousingType" context="FV.S"></trans-label>
							</specsheet-feature>` : ''}

					${shouldDisplayFeature(this.t('LE_ColourTemperature_1', 'FV.S')) ?
					html`
                        <specsheet-feature class="specsheet">" >
                            <!-- Lichtfarbe -->
                            <trans-label slot="label" key="LE_ColourTemperature_1" context="F"></trans-label>
                            <span slot="value">
								<trans-label key="LE_ColourTemperature_1" context="FV.S" prefix="LED "></trans-label><br>
								<trans-label key="LE_ColourTemperature_2" context="FV.S" prefix="LED "></trans-label>
							</span>
                        </specsheet-feature>` : ''}


                        <specsheet-feature class="specsheet">" >
                            <!-- Systemlumen -->
                            <trans-label slot="label" key="LU_SystemLuminousFlux" context="D"></trans-label>
                            <span slot="value">
								${selectSystemLumen(s) + ' ' + this.t('LU_SystemLuminousFluxUnit.LM', 'DV.S')}
								${!selectIsOnesided(s) ? '(' + selectSystemLumenString(s) + ' ' + this.t('LU_SystemLuminousFluxUnit.LM', 'DV.S') + ')' : null}
							</span>
                        </specsheet-feature>


						${isDescAttPresent? html`
						<specsheet-feature class="specsheet">" >
							<trans-label slot="label" class="label" key="LE_CRI" context="D"></trans-label>
							<span  slot="value" class="value">${descAtt}</span>
						</specsheet-feature>` : ''}

                        <specsheet-feature class="specsheet">" >
                            <!-- Systemleistung -->
                            <trans-label slot="label" key="LU_SystemWattage" context="D"></trans-label>
                            <span slot="value">
								${selectSystemPower(s)} <trans-label key="LU_SystemWattageUnit.W" context="DV.S"></trans-label>
								${!selectIsOnesided(s) ? '(' + selectSystemPowerString(s) + ' ' + this.t('LU_SystemWattageUnit.W', 'DV.S') + ')' : null}
							</span>
                        </specsheet-feature>

                        <specsheet-feature class="specsheet">" >
                            <!-- Lichtausbeute -->
                            <trans-label slot="label" key="LU_Efficacy" context="D"></trans-label>
                            <span slot="value">${selectSystemLumenPerPower(s)} <trans-label key="LU_EfficacyUnit.lm/W"
                                                                                            context="DV.S"></trans-label></span>
                        </specsheet-feature>

                    </specsheet-featuregroup>

                    <specsheet-featuregroup>

                        ${when(!selectIsSingle(s),
                                () => html`
                                    <specsheet-feature class="specsheet">" >
                                        <!-- Lichtverteilung -->
                                        <trans-label slot="label" key="HS_Construction" context="F"></trans-label>
                                        <trans-label slot="value" key="HS_Construction" context="FV.S"></trans-label>
                                    </specsheet-feature>
                                `,
                                () => html`
                                `)}

                        <specsheet-feature class="specsheet">" >
                            <trans-label slot="label" key="LE_LightEngineDescription" context="D"></trans-label>
                            <span slot="value">
							${filterAndJoin([
								this.t('LE_LightEngineModuleSize_1', 'FV.S'),
								this.t('LE_LightDistribution_1', 'FV.S'),
								this.t('LE_OpticalVersion_1', 'FV.S'),
								this.t('LE_ReflectorColour_1', 'FV.S'),
							], ', ')}

							${when(!selectIsOnesided(s), () => html`<br>
							${filterAndJoin([
								this.t('LE_LightEngineModuleSize_2', 'FV.S'),
								this.t('LE_LightDistribution_2', 'FV.S'),
								this.t('LE_OpticalVersion_2', 'FV.S'),
								this.t('LE_ReflectorColour_2', 'FV.S'),
							], ', ')}
							`)}
						</span>
                        </specsheet-feature>

						${shouldDisplayFeature(this.t('HS_LightEmission', 'FV.S')) ?
						html`
                        <specsheet-feature class="specsheet">" >
                            <trans-label slot="label" key="HS_LightEmission" context="F"></trans-label>
                            <trans-label slot="value" key="HS_LightEmission" context="FV.S"></trans-label>
                        </specsheet-feature>` : ''}

                    </specsheet-featuregroup>

                    <specsheet-featuregroup>

						${shouldDisplayFeature(this.c('EB_EBlockOperatingGearDescription.ElectronicConverter"', 'DV.S')) ?
						html`
                        <specsheet-feature class="specsheet">" >
                            <!-- Elektrische Ausführung -->
                            <trans-label slot="label" key="EB_EBlockOperatingGearDescription" context="D"></trans-label>
                            <trans-label slot="value" key="EB_EBlockOperatingGearDescription.ElectronicConverter" context="DV.S"></trans-label>
                        </specsheet-feature>` : ''}


						${shouldDisplayFeature(this.c('LU_SurgeProtection.10 kV"', 'DV.S')) ?
						html`
                        <specsheet-feature class="specsheet">" >
                            <!-- Überspannungsschutz -->
                            <trans-label slot="label" key="LU_SurgeProtection" context="D"></trans-label>
                            <trans-label slot="value" key="LU_SurgeProtection.10 kV" context="DV.S"></trans-label>
                        </specsheet-feature>` : ''}


						${shouldDisplayFeature(this.t('EB_Control_1', 'FV.S')) ?
						html`
                        <specsheet-feature class="specsheet">" >
                            <!-- Steuerung -->
                            <trans-label slot="label" key="EB_Control_1" context="F"></trans-label>
                            <trans-label slot="value" key="EB_Control_1" context="FV.S"></trans-label>
                        </specsheet-feature>` : ''}


						${shouldDisplayFeature(this.t('EB_Zhaga_1', 'FV.S')) ?
						html`
                        <specsheet-feature class="specsheet">" >
                            <!-- Zhaga -->
                            <trans-label slot="label" key="EB_Zhaga_1" context="F"></trans-label>
                            <trans-label slot="value" key="EB_Zhaga_1" context="FV.S"></trans-label>
                        </specsheet-feature>` : ''}


						${shouldDisplayFeature(this.t('EB_ConstantLumenTechnology_1', 'FV.S')) ?
						html`
                        <specsheet-feature class="specsheet">" >
                            <!-- CLT -->
                            <trans-label slot="label" key="EB_ConstantLumenTechnology_1" context="F"></trans-label>
                            <trans-label slot="value" key="EB_ConstantLumenTechnology_1" context="FV.S"></trans-label>
                        </specsheet-feature>` : ''}


						${shouldDisplayFeature(this.c('LU_TechnicalRegion.220-240V 50/60 Hz', 'DV.S')) ?
						html`
                        <specsheet-feature class="specsheet">" >
                            <!-- Frequenz/Spannung -->
                            <trans-label slot="label" key="LU_TechnicalRegion" context="D"></trans-label>
                            <trans-label slot="value" key="LU_TechnicalRegion.220-240V 50/60 Hz" context="DV.S"></trans-label>
                        </specsheet-feature>` : ''}

                    </specsheet-featuregroup>

                    <specsheet-featuregroup>


					${shouldDisplayFeature(this.t('HS_HousingColour', 'FV.S')) ?
						html`
                        <specsheet-feature class="specsheet">" >
                            <!-- Gehäusefarbe -->
                            <trans-label slot="label" key="HS_HousingColour" context="F"></trans-label>
                            <trans-label slot="value" key="HS_HousingColour" context="FV.S"></trans-label>
                        </specsheet-feature>` : ''}


						${this.c('DescriptiveAttributeReference.LU_LuminaireMaterial') ? html`
						<specsheet-feature class="specsheet">" >
							<!-- Gehäuse -->
							<trans-label slot="label" key="LU_LuminaireMaterial" context="D"></trans-label>
							<trans-label slot="value" .key="${this.c('DescriptiveAttributeReference.LU_LuminaireMaterial')}"
										 context="DV.S"></trans-label>
						</specsheet-feature>
					` : ''}

						${shouldDisplayFeature(this.t('HS_HousingCoating', 'FV.S')) ?
						html`
                        <specsheet-feature class="specsheet">" >
                            <!-- Oberfläche -->
                            <trans-label slot="label" key="HS_HousingCoating" context="F"></trans-label>
                            <trans-label slot="value" key="HS_HousingCoating" context="FV.S"></trans-label>
                        </specsheet-feature>` : ''}


						${shouldDisplayFeature(this.t('HS_Base', 'FV.S')) ?
						html`
                        <specsheet-feature class="specsheet">" >
                            <!-- Gründung -->
                            <trans-label slot="label" key="HS_Base" context="F"></trans-label>
                            <trans-label slot="value" key="HS_Base" context="FV.S"></trans-label>
                        </specsheet-feature>` : ''}

						<specsheet-feature class="specsheet">" >
						<trans-label slot="label" key="HS_HousingPowerConnection" context="F"></trans-label>
						<!-- Ausstattung -->
						${when(!selectIsSingle(s),
							() => html`
								<span slot="value">
									<trans-label key="HS_NumberPoleDoors" context="FV.S"></trans-label><br>
									<trans-label key="HS_HousingMountingLocationEblock_1" context="F"></trans-label>: <trans-label
										key="EB_EBlockMountingType_1" context="FV.S"></trans-label>
									${when(!selectIsOnesided(s), () => html`
										<br>
										<trans-label key="HS_HousingMountingLocationEblock_2" context="F"></trans-label>:
										<trans-label key="EB_EBlockMountingType_2" context="FV.S"></trans-label>
									`)}
									<br>
									<trans-label .key="${this.c('DescriptiveAttributeReference.LU_FuseBox')}" context="DV.S"></trans-label>
								</span>
							`,
							() => html`
								<span slot="value">
									<trans-label key="HS_HousingPowerConnection" context="FV.S"></trans-label>
								</span>
							`
						)}
					</specsheet-feature>


                    </specsheet-featuregroup>

                    <specsheet-featuregroup>
					${shouldDisplayFeature(this.t('HS_HousingHeight', 'FV.S')) ?
					html`
                        <specsheet-feature class="specsheet">" >
                            <!-- Höhe -->
                            <trans-label slot="label" key="HS_HousingHeight" context="F"></trans-label>
                            <trans-label slot="value" key="HS_HousingHeight" context="FV.S"></trans-label>
                        </specsheet-feature>` : ''}

					${(this.t('HS_HousingDiameter.114', 'DV.S') !== '' || this.t('HS_HousingDiameterUnit.MM', 'DV.S') !== '') ?
					html`
					<specsheet-feature class="specsheet">" >
						<!-- Durchmesser -->
						<trans-label slot="label" key="HS_HousingDiameterUnit" context="D"></trans-label>
						<span slot="value">
							<trans-label key="HS_HousingDiameter.114" context="DV.S"></trans-label>
							<trans-label key="HS_HousingDiameterUnit.MM" context="DV.S"></trans-label>
						</span>
					</specsheet-feature>
					` : ''}


						${shouldDisplayFeature(this.c('LU_LuminaireWeightUnit.KG', 'DV.S')) ?
						html`
                        <specsheet-feature class="specsheet">" >
                            <!-- Gewicht -->
                            <trans-label slot="label" key="LU_LuminaireWeight" context="D"></trans-label>
                            <span slot="value" class=${classes('specsheet__value')}>
							${selectWeight(s)}
							<trans-label key="LU_LuminaireWeightUnit.KG" context="DV.S"></trans-label><br>
							</span>
                        </specsheet-feature>` : ''}

						${shouldDisplayFeature(this.c('HS_WindSurfaceAreaUnit.M2', 'DV.S')) ?
						html`
                        <specsheet-feature class="specsheet">" >
                            <!-- Windangriffsfläche -->
                            <trans-label slot="label" key="HS_WindSurfaceArea" context="D"></trans-label>
                            <span slot="value">
							${selectWindsurfacearea(s)}
							<trans-label key="HS_WindSurfaceAreaUnit.M2" context="DV.S"></trans-label>
							</span>
                        </specsheet-feature>` : ''}

						${isStandardsPresent ? html`
						<specsheet-feature class="specsheet">" >
						  <trans-label slot="label" class="label" key="LU_ApplicableStandards" context="D"></trans-label>
						  <span    slot="value" class="value">
							${filterAndJoin(filteredStandards, ', ')}
						  </span>
						</specsheet-feature>` : ''}

                    </specsheet-featuregroup>
                </div>
		`;
	}
}
